import * as React from 'react'
import { FC, useCallback, useState } from 'react'
import { Field, setIn, useFormikContext } from 'formik'
import { HereResult } from '@user/store/services/hereGeoCoder'
import { PlaceList } from './PlaceList'
import { FormAddress, hereResultToFormAddress } from '../typings'
import { useQueryPlaces } from '../hooks'
import { BaseTextField } from '../../BaseTextField'
import { TextFieldProps } from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'

interface Props {
  setGeocode: (geocode: boolean) => void
  path: string
  textFieldProps?: TextFieldProps
  disabled?: boolean
  required?: boolean
  enforcePPLengthRequirements?: boolean
  avoidValidateOnBlur?: boolean
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void
  onAddressBlur?: () => void
  onSelect?: (place?: HereResult) => void
}

export const AddressSearch: FC<Props> = (props) => {
  const {
    setGeocode,
    path,
    textFieldProps,
    disabled,
    required = false,
    enforcePPLengthRequirements = false,
    avoidValidateOnBlur = false,
    setFieldTouched,
    onAddressBlur,
    onSelect,
  } = props
  const { setValues, handleChange } = useFormikContext<FormAddress>()

  const { validatePostalCode } = useLocalisedValidations()

  // state for place select list
  const [open, setOpen] = useState(false)
  const onOpen = useCallback(() => {
    if (!disabled) setOpen(true)
  }, [disabled])
  const onClose = useCallback(() => setOpen(false), [])

  const { results: places, execute: search } = useQueryPlaces()

  const onChangeAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      handleChange(event)
      search(value)
      setOpen(true)
      setGeocode(true)
    },
    [search, handleChange, setGeocode],
  )

  const onSelectPlace = useCallback(
    (place: HereResult) => {
      const address: FormAddress = hereResultToFormAddress(place)

      if (enforcePPLengthRequirements) {
        // overflow addr line 1 into line 2
        address.address2 =
          address.address2.length < 1 && address.address1.length > 30
            ? address.address1
                .substring(30, address.address1.length)
                .substring(0, 29)
            : address.address2
        address.address1 = address.address1.substring(0, 29)

        address.city = address.city.substring(0, 19)
        address.state = address.state.substring(0, 9)
        address.postalCode = address.postalCode.substring(0, 9) ?? ''
        address.country = address.country.substring(0, 19)
      }

      setValues((current) => {
        let next = { ...current }

        next = setIn(next, `${path}address1`, address.address1 ?? '')
        next = setIn(next, `${path}address2`, address.address2 ?? '')

        next = setIn(next, `${path}city`, address.city ?? '')
        next = setIn(next, `${path}state`, address.state ?? '')
        next = setIn(next, `${path}postalCode`, address.postalCode ?? '')

        next = setIn(next, `${path}country`, address.country)

        next = setIn(next, `${path}lat`, address.lat)
        next = setIn(next, `${path}lng`, address.lng)

        return next
      })

      if (onSelect) {
        onSelect(place)
      }

      setOpen(false)
      setGeocode(true)
    },
    [
      enforcePPLengthRequirements,
      path,
      setGeocode,
      setValues,
      setFieldTouched,
      validatePostalCode,
    ],
  )

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key == 'Escape') {
      setOpen(false)
    }
  }, [])

  return (
    <FormControl fullWidth>
      <Field
        name={`${path}address1`}
        label="Address"
        component={BaseTextField}
        onClick={onOpen}
        onChange={onChangeAddress}
        onKeyDown={onKeyDown}
        onBlur={onAddressBlur ?? undefined}
        disabled={disabled}
        required={required}
        {...textFieldProps}
        avoidValidateOnBlur={avoidValidateOnBlur}
        data-test-id={`${path}-address1`}
      />
      <PlaceList
        open={open}
        onClose={onClose}
        onSelectPlace={onSelectPlace}
        places={places}
        margin={textFieldProps?.margin}
      />
    </FormControl>
  )
}
AddressSearch.displayName = 'AddressSearch'
